import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosProgressEvent } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
	Navigate,
	useLocation,
	useNavigate,
	useParams,
} from 'react-router-dom';
import * as yup from 'yup';

import { useAuth } from '../../hooks/useAuth';
import useFetch from '../../hooks/useFetch';
import useSimpleMutation from '../../hooks/useSimpleMutation';
import { useUnitats } from '../../hooks/useUnitats';
import { extractErrorMessage } from '../../utils/errorHandling';
import {
	LoadingGif,
	LoadingMessage,
	PageTitle,
	StandardContentContainer,
} from '../Shared/CommonStyles';
import {
	Button,
	ErrorMessage,
	FormContainer,
	FormGroup,
	Input,
	Label,
	Select,
} from '../Shared/FormStyles';

import * as S from './AlbumEditForm.styles';

interface AlbumData {
	_id: string;
	name: string;
	upload_date: string;
	unitat: string;
	schoolYear?: string;
	isProvisional?: boolean;
	picture_count?: number;
}

interface AlbumEditFormData {
	name: string;
	upload_date?: string;
	schoolYear?: string | null;
	customSchoolYear?: string;
	unitat: string;
	isProvisional?: boolean;
}

const schema = yup.object().shape({
	name: yup.string().required("El nom de l'àlbum és obligatori."),
	upload_date: yup
		.string()
		.optional()
		.test(
			'is-valid-date',
			'La data no és vàlida',
			(value) => !value || !isNaN(Date.parse(value))
		),
	schoolYear: yup.string().nullable(),
	customSchoolYear: yup.string().when('schoolYear', {
		is: 'custom',
		then: (schema) =>
			schema
				.required('Introdueix un any')
				.matches(/^\d{4}-\d{4}$/, 'Format incorrecte (ex: 1990-1991)'),
		otherwise: (schema) => schema.notRequired(),
	}),
	unitat: yup.string().required('Has de seleccionar una unitat'),
	isProvisional: yup.boolean(),
});

const generateSchoolYears = () => {
	const currentYear = new Date().getFullYear();
	const currentMonth = new Date().getMonth();

	const startYear = currentMonth >= 8 ? currentYear : currentYear - 1;
	const years = [];

	for (let y = startYear; y >= 2000; y--) {
		years.push(`${y}-${y + 1}`);
	}
	return years;
};

const AlbumEditForm: React.FC = () => {
	const { id: albumId } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const location = useLocation();
	// Where the user came from (category they were browsing, e.g. 'classificar').
	// Set by GalleryContainer via <Link state={{ category: ... }}>. Falls back
	// to the unitat view when absent (deep-link/refresh).
	const fromCategory = (
		(location.state as { category?: string } | null)?.category ?? ''
	).toString();
	const { user } = useAuth();
	const {
		unitats,
		isFetchingUnitats,
		fetchUnitatsError,
	} = useUnitats();
	const [formError, setFormError] = useState<string | null>(null);
	const [uploadProgress, setUploadProgress] = useState<number>(0);
	const [selectedZipFile, setSelectedZipFile] = useState<File | null>(null);
	const schoolYears = useMemo(() => generateSchoolYears(), []);

	const {
		data: albumData,
		isLoading: isLoadingAlbum,
		error: fetchError,
	} = useFetch<AlbumData>({
		queryKey: ['album', albumId],
		url: `/api/galeria/albums/${albumId}`,
	});

	// Authorization guard: determine if the user can edit this album
	const canEditAlbum = useMemo(() => {
		if (!user || !albumData) return false;
		// Admin, Agrupament, Caps can edit any album
		if (
			user.roles.includes('Admin') ||
			user.roles.includes('Agrupament') ||
			user.roles.includes('Caps') // trusted curators: any album
		) {
			return true;
		}
		// Unitat can only edit albums belonging to their own unitat
		if (user.roles.includes('Unitat') && user.unitat === albumData.unitat) {
			return true;
		}
		return false;
	}, [user, albumData]);

	const {
		register,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm<AlbumEditFormData>({
		resolver: yupResolver(schema),
		defaultValues: {
			name: '',
			upload_date: '',
			schoolYear: '',
			unitat: '',
			isProvisional: false,
		},
	});

	const selectedSchoolYear = watch('schoolYear');

	useEffect(() => {
		if (albumData) {
			let sy = albumData.schoolYear || '';
			let customSy = '';

			if (sy) {
				if (!schoolYears.includes(sy)) {
					customSy = sy;
					sy = 'custom';
				}
			}

			reset({
				name: albumData.name,
				upload_date: albumData.upload_date
					? new Date(albumData.upload_date)
							.toISOString()
							.split('T')[0]
					: '',
				schoolYear: sy,
				customSchoolYear: customSy,
				unitat: albumData.unitat,
				isProvisional: albumData.isProvisional ?? false,
			});
		}
	}, [albumData, reset, schoolYears]);

	const updateDetailsMutation = useSimpleMutation<
		Partial<AlbumEditFormData>,
		AlbumData
	>({
		method: 'put',
		url: `/api/galeria/albums/${albumId}/details`,
		queriesToInvalidate: [
			['album', albumId],
			// The album can MOVE buckets (e.g. out of 'classificar' into a unitat
			// or vice-versa) — invalidate the whole 'albums' prefix, not just the
			// destination bucket, or the source list stays stale.
			['albums'],
			['classification-overview'],
			['album-slideshow-thumbnails'],
			['category-thumbnails'],
		],
	});

	const addPicturesMutation = useSimpleMutation<FormData, unknown>({
		method: 'post',
		url: `/api/galeria/albums/${albumId}/add-pictures`,
		onUploadProgress: (progressEvent: AxiosProgressEvent) => {
			const percentCompleted = progressEvent.total
				? Math.round((progressEvent.loaded * 100) / progressEvent.total)
				: 0;
			setUploadProgress(percentCompleted);
		},
		queriesToInvalidate: [
			['album', albumId],
			['pictures', `album${albumId}`],
			['albums', albumData?.unitat],
			['albums', 'tots'],
			// New pictures can land in ANY gallery view (all-pictures bucket is
			// 'totes', unitat buckets are `unitatX`) — invalidate the whole
			// 'pictures' prefix; the old object-keyed entries matched NOTHING
			// (keys are positional; sortField is a plain string element).
			['pictures'],
		],
	});

	const onSubmit = async (data: AlbumEditFormData) => {
		setFormError(null);
		setUploadProgress(0);

		const zipFileList = selectedZipFile ? [selectedZipFile] : null;

		if (zipFileList && zipFileList.length > 0) {
			const file = zipFileList[0];
			const allowedTypes = [
				'application/zip',
				'application/x-zip-compressed',
			];
			const maxSize = 2 * 1024 * 1024 * 1024; // 2GB

			if (!allowedTypes.includes(file.type)) {
				setFormError('Arxiu invàlid: Només es permeten arxius .zip');
				return;
			}
			if (file.size > maxSize) {
				setFormError(
					'Arxiu invàlid: L&apos;arxiu és massa gran (màxim 2GB)'
				);
				return;
			}
		}

		let detailsUpdated = false;
		let picturesAdded = false;
		let finalNavigationUrl = fromCategory
			? `/galeria/${fromCategory}/${albumId}`
			: `/galeria/${data.unitat}/${albumId}`;

		try {
			let finalSchoolYear = data.schoolYear;
			if (finalSchoolYear === 'custom') {
				finalSchoolYear = data.customSchoolYear || '';
			}

			const detailsChanged =
				data.name !== albumData?.name ||
				(data.upload_date &&
					new Date(data.upload_date).toISOString().split('T')[0] !==
						new Date(albumData?.upload_date ?? '')
							.toISOString()
							.split('T')[0]) ||
				finalSchoolYear !== (albumData?.schoolYear || '') ||
				data.unitat !== albumData?.unitat ||
				data.isProvisional !== (albumData?.isProvisional ?? false);

			if (detailsChanged) {
				await updateDetailsMutation.mutateAsync({
					name: data.name,
					upload_date: data.upload_date || undefined,
					schoolYear: finalSchoolYear,
					unitat: data.unitat,
					isProvisional: data.isProvisional,
				});
				detailsUpdated = true;
			}

			if (selectedZipFile) {
				const formData = new FormData();
				formData.append('albumFile', selectedZipFile);

				await addPicturesMutation.mutateAsync(formData);
				picturesAdded = true;
			}

			if (detailsUpdated || picturesAdded) {
				navigate(finalNavigationUrl);
			} else {
				navigate(finalNavigationUrl);
			}
		} catch (err: unknown) {
			let message = 'Error desconegut';
			if (err instanceof Error) {
				if ('isAxiosError' in err && err.isAxiosError) {
					const axiosError = err as import('axios').AxiosError<{
						message?: string;
					}>;
					message = extractErrorMessage(
						axiosError.response?.data?.message || axiosError.message
					);
				} else {
					message = extractErrorMessage(err.message);
				}
			} else if (typeof err === 'string') {
				message = extractErrorMessage(err);
			}
			setFormError(message);
			setUploadProgress(0);
		}
	};

	const isMutating =
		updateDetailsMutation.isPending || addPicturesMutation.isPending;
	const combinedLoading =
		isLoadingAlbum ||
		isFetchingUnitats;
	const error = fetchError || fetchUnitatsError;

	// Only show full page loader if we are loading data AND NOT mutating
	if (combinedLoading && !error && !isMutating) {
		return (
			<StandardContentContainer>
				<LoadingMessage>
					<LoadingGif size="large" />
				</LoadingMessage>
			</StandardContentContainer>
		);
	}

	if (error) {
		return (
			<StandardContentContainer>
				<ErrorMessage>
					Error carregant dades: {extractErrorMessage(error)}
				</ErrorMessage>
			</StandardContentContainer>
		);
	}

	if (!albumData) {
		return (
			<StandardContentContainer>
				<ErrorMessage>No s&apos;ha trobat l&apos;àlbum.</ErrorMessage>
			</StandardContentContainer>
		);
	}

	// Authorization guard: redirect if user doesn't have permission to edit this album
	if (!canEditAlbum) {
		return <Navigate to="/galeria" replace />;
	}

	return (
		<StandardContentContainer>
			<PageTitle>Editar àlbum: {albumData.name}</PageTitle>
			<FormContainer onSubmit={handleSubmit(onSubmit)}>
				<FormGroup>
					<Label htmlFor="name">Nom de l&apos;àlbum</Label>
					<Input id="name" type="text" {...register('name')} />
					{errors.name && (
						<ErrorMessage>{errors.name.message}</ErrorMessage>
					)}
				</FormGroup>

				<FormGroup>
					<Label htmlFor="unitat">Unitat</Label>
					<Select id="unitat" {...register('unitat')}>
						{(user?.roles.includes('Admin') ||
							user?.roles.includes('Agrupament') ||
							user?.roles.includes('Caps')) && (
							<option value="agrupament">Agrupament</option>
						)}
						{unitats?.map((unitat) => (
							<option
								key={unitat._id}
								value={unitat.abreviacio.toLowerCase()}
								disabled={
									!(
										user?.roles.includes('Admin') ||
										user?.roles.includes('Agrupament') ||
										user?.roles.includes('Caps') ||
										user?.unitat ===
											unitat.abreviacio.toLowerCase()
									)
								}
							>
								{unitat.nom}
							</option>
						))}
					</Select>
					{errors.unitat && (
						<ErrorMessage>
							{errors.unitat.message?.toString()}
						</ErrorMessage>
					)}
				</FormGroup>

				<FormGroup>
					<Label htmlFor="schoolYear">Curs</Label>
					<Select id="schoolYear" {...register('schoolYear')}>
						{schoolYears.map((year) => (
							<option key={year} value={year}>
								{year}
							</option>
						))}
						<option value="">Varis / Desconegut</option>
						<option value="custom">Més antic...</option>
					</Select>
					{errors.schoolYear && (
						<ErrorMessage>{errors.schoolYear.message}</ErrorMessage>
					)}
				</FormGroup>

				{selectedSchoolYear === 'custom' && (
					<FormGroup>
						<Label htmlFor="customSchoolYear">
							Escriu el curs
						</Label>
						<Input
							id="customSchoolYear"
							placeholder="1990-1991"
							{...register('customSchoolYear')}
						/>
						{errors.customSchoolYear && (
							<ErrorMessage>
								{errors.customSchoolYear.message}
							</ErrorMessage>
						)}
					</FormGroup>
				)}

				<FormGroup>
					<Label htmlFor="upload_date">
						Data de creació (opcional)
					</Label>
					<Input
						id="upload_date"
						type="date"
						{...register('upload_date')}
					/>
					{errors.upload_date && (
						<ErrorMessage>
							{errors.upload_date.message}
						</ErrorMessage>
					)}
				</FormGroup>

				{(user?.roles.includes('Admin') ||
					user?.roles.includes('Agrupament') ||
					user?.roles.includes('Caps')) && (
					<FormGroup>
						<Label htmlFor="isProvisional">
							<input
								id="isProvisional"
								type="checkbox"
								{...register('isProvisional')}
								style={{ marginRight: '0.5rem' }}
							/>
							Marcar com a provisional (visible només per Admin/Agrupament)
						</Label>
					</FormGroup>
				)}

				<FormGroup>
					<Label htmlFor="zipFile">
						Afegir més fotos (arxiu .zip, opcional)
					</Label>
					<Input
						id="zipFile"
						type="file"
						accept=".zip"
						onChange={(e) =>
							setSelectedZipFile(
								e.target.files ? e.target.files[0] : null
							)
						}
					/>
					{addPicturesMutation.isPending && uploadProgress > 0 && (
						<div>Pujant: {uploadProgress}%</div>
					)}
				</FormGroup>

				{formError && <ErrorMessage>{formError}</ErrorMessage>}
				{updateDetailsMutation.error && (
					<ErrorMessage>
						Error actualitzant detalls:{' '}
						{extractErrorMessage(updateDetailsMutation.error)}
					</ErrorMessage>
				)}
				{addPicturesMutation.error && (
					<ErrorMessage>
						Error afegint fotos:{' '}
						{extractErrorMessage(addPicturesMutation.error)}
					</ErrorMessage>
				)}

				<S.ButtonGroup>
					<Button
						type="submit"
						disabled={combinedLoading || isMutating}
					>
						{addPicturesMutation.isPending
							? `Pujant... ${uploadProgress}%`
							: isMutating
							? 'Guardant...'
							: 'Guardar canvis'}
					</Button>
					<Button
						type="button"
						onClick={() =>
							navigate(
								fromCategory
									? `/galeria/${fromCategory}/${albumId}`
									: `/galeria/${albumData.unitat}/${albumId}`
							)
						}
						disabled={combinedLoading || isMutating}
						style={{ backgroundColor: '#6c757d' }}
					>
						Cancel·lar
					</Button>
				</S.ButtonGroup>
			</FormContainer>
		</StandardContentContainer>
	);
};

export default AlbumEditForm;
